import { useEffect, useId, useMemo, useState } from 'react';
import { fetchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { useI18n } from '../i18n';
import { isIsraelCountryId } from '../utils/assetCreateFormDefaults';
import {
  cardPaymentIsValid,
  isPayplusTestCardNumber,
  isValidIsraeliNationalId,
  type CardPaymentFieldErrors,
  validateCardPayment,
} from '../utils/bookingPaymentValidate';
import { resolveProfileCountryIdOrIsrael } from '../utils/profileAssetOwnerEdit';
import {
  formatPaymentCardLast4Display,
  getStoredProfilePaymentCards,
  type ProfileSavedPaymentCard,
} from '../utils/profilePaymentCard';
import { PaymentCardBrandIcon } from './PaymentCardBrandIcon';
import styles from './BookingPaymentPanel.module.css';

type BookingPaymentPayResult =
  | boolean
  | Promise<boolean>
  | { success: boolean; errorMessage?: string }
  | Promise<{ success: boolean; errorMessage?: string }>;

export type BookingPaymentPayOptions = {
  /** True when the PayPlus sandbox test card was entered — charge ₪1 only. */
  payplusTestCard?: boolean;
};

type BookingPaymentPanelProps = {
  /** Pre-formatted amount due (e.g. "₪120"), or null when the total is unknown. */
  amountLabel: string | null;
  /** Invoked once payment details validate; resolves when the booking is created. */
  onPay: (options?: BookingPaymentPayOptions) => BookingPaymentPayResult;
  onCancel: () => void;
  processing?: boolean;
  /** Private-client wallet spend — when set, offers a wallet credits payment method. */
  onPayWithWalletCredits?: () => BookingPaymentPayResult;
  /** Optional label for current wallet balance (shown on the wallet method). */
  walletCreditsBalanceLabel?: string | null;
};

type PaymentMethod = 'card' | 'bit' | 'paypal' | 'wallet';
type CardEntryMode = 'saved' | 'new';

function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

function formatCardNumber(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

function formatExpiry(value: string): string {
  const digits = digitsOnly(value).slice(0, 4);
  if (digits.length <= 2) {
    return digits;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatIsraeliMobile(value: string): string {
  const digits = digitsOnly(value).slice(0, 10);
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidIsraeliMobile(raw: string): boolean {
  const digits = digitsOnly(raw);
  return /^05\d{8}$/.test(digits);
}

function isValidEmail(raw: string): boolean {
  const trimmed = raw.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

function firstFieldError(errors: CardPaymentFieldErrors): string | null {
  return (
    errors.nationalId ??
    errors.cardName ??
    errors.cardNumber ??
    errors.expiry ??
    errors.cvc ??
    null
  );
}

export function BookingPaymentPanel({
  amountLabel,
  onPay,
  onCancel,
  processing = false,
  onPayWithWalletCredits,
  walletCreditsBalanceLabel = null,
}: BookingPaymentPanelProps) {
  const { t, language } = useI18n();
  const fieldId = useId();
  const isHebrew = language === 'he';
  const dir = isHebrew ? 'rtl' : 'ltr';
  const [isIsraelProfile, setIsIsraelProfile] = useState(true);
  const [savedCards, setSavedCards] = useState<ProfileSavedPaymentCard[]>([]);
  const [selectedSavedCardId, setSelectedSavedCardId] = useState<string | null>(null);
  const [cardEntryMode, setCardEntryMode] = useState<CardEntryMode>('new');
  const [method, setMethod] = useState<PaymentMethod>('card');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [bitPhone, setBitPhone] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cardFieldErrors, setCardFieldErrors] = useState<CardPaymentFieldErrors>({});

  useEffect(() => {
    if (onPayWithWalletCredits) {
      setMethod('wallet');
    }
  }, [onPayWithWalletCredits]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchProfile();
        if (!cancelled) {
          setIsIsraelProfile(isIsraelCountryId(resolveProfileCountryIdOrIsrael(data)));
        }
      } catch {
        if (!cancelled) {
          setIsIsraelProfile(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cards = getStoredProfilePaymentCards(getStoredLoginIdentifier());
    setSavedCards(cards);
    if (cards.length > 0) {
      const primary = cards.find((card) => card.isPrimary) ?? cards[0];
      setSelectedSavedCardId(primary.id);
      setCardEntryMode('saved');
      if (primary.nationalId) {
        setNationalId(primary.nationalId);
      }
    }
  }, []);

  const methods = useMemo((): PaymentMethod[] => {
    const list: PaymentMethod[] = [];
    if (onPayWithWalletCredits) {
      list.push('wallet');
    }
    list.push('card');
    if (isIsraelProfile) {
      list.push('bit');
    }
    list.push('paypal');
    return list;
  }, [isIsraelProfile, onPayWithWalletCredits]);

  const activeMethod = methods.includes(method) ? method : methods[0] ?? 'card';
  const selectedSavedCard =
    savedCards.find((card) => card.id === selectedSavedCardId) ?? null;
  const usingSavedCard = Boolean(
    activeMethod === 'card' && cardEntryMode === 'saved' && selectedSavedCard,
  );
  const cardMessages = useMemo(
    () => ({
      errorNationalId: t('bookings.payment.errorNationalId'),
      errorName: t('bookings.payment.errorName'),
      errorNameInvalid: t('bookings.payment.errorNameInvalid'),
      errorNumber: t('bookings.payment.errorNumber'),
      errorNumberLuhn: t('bookings.payment.errorNumberLuhn'),
      errorExpiry: t('bookings.payment.errorExpiry'),
      errorExpiryPast: t('bookings.payment.errorExpiryPast'),
      errorCvc: t('bookings.payment.errorCvc'),
    }),
    [t],
  );

  /** Israel: card skips our form and opens PayPlus. Other countries keep local card entry. */
  const israelCardGoesToPayplus = isIsraelProfile && activeMethod === 'card';

  const subtitleKey =
    activeMethod === 'wallet'
      ? 'bookings.walletCredits.subtitle'
      : activeMethod === 'card'
        ? israelCardGoesToPayplus
          ? 'bookings.payment.subtitleCardPayplus'
          : 'bookings.payment.subtitleCard'
        : activeMethod === 'bit'
          ? 'bookings.payment.subtitleBit'
          : 'bookings.payment.subtitlePaypal';

  const validateCard = (): CardPaymentFieldErrors => {
    if (usingSavedCard) {
      const errors: CardPaymentFieldErrors = {};
      const cvcDigits = digitsOnly(cvc);
      if (cvcDigits.length < 3 || cvcDigits.length > 4) {
        errors.cvc = cardMessages.errorCvc;
      }
      if (isIsraelProfile && !isValidIsraeliNationalId(nationalId)) {
        errors.nationalId = cardMessages.errorNationalId;
      }
      return errors;
    }
    return validateCardPayment(
      {
        cardName,
        cardNumber,
        expiry,
        cvc,
        nationalId,
        requireNationalId: isIsraelProfile,
      },
      cardMessages,
    );
  };

  const validateBit = (): string | null => {
    if (isIsraelProfile && !isValidIsraeliNationalId(nationalId)) {
      return t('bookings.payment.errorNationalId');
    }
    if (!isValidIsraeliMobile(bitPhone)) {
      return t('bookings.payment.errorBitPhone');
    }
    return null;
  };

  const validatePaypal = (): string | null => {
    if (!isValidEmail(paypalEmail)) {
      return t('bookings.payment.errorPaypalEmail');
    }
    return null;
  };

  const applyPayResult = (
    payResult: boolean | { success: boolean; errorMessage?: string },
  ) => {
    const payFailed =
      typeof payResult === 'boolean' ? !payResult : payResult.success === false;
    if (payFailed) {
      const detail =
        typeof payResult === 'object' && payResult !== null && 'errorMessage' in payResult
          ? payResult.errorMessage
          : undefined;
      setError(detail ?? t('bookings.payment.errorBookingFailed'));
    }
  };

  const runGatewayPay = async (options?: BookingPaymentPayOptions) => {
    setError(null);
    setCardFieldErrors({});
    const payResult = await onPay(options);
    applyPayResult(payResult);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (processing) {
      return;
    }

    if (activeMethod === 'wallet') {
      if (!onPayWithWalletCredits) {
        setError(t('common.somethingWrong'));
        return;
      }
      setError(null);
      const payResult = await onPayWithWalletCredits();
      applyPayResult(payResult);
      return;
    }

    if (activeMethod === 'card') {
      if (isIsraelProfile) {
        await runGatewayPay();
        return;
      }
      const fieldErrors = validateCard();
      setCardFieldErrors(fieldErrors);
      if (!cardPaymentIsValid(fieldErrors)) {
        setError(firstFieldError(fieldErrors));
        return;
      }
      const payplusTestCard = usingSavedCard
        ? false
        : isPayplusTestCardNumber(cardNumber);
      if (payplusTestCard) {
        console.info(
          '[PayPlus test] Sandbox card detected — booking will be created for ₪1 only (≤ ₪5 sandbox limit).',
        );
      }
      await runGatewayPay(payplusTestCard ? { payplusTestCard: true } : undefined);
      return;
    }

    const validationError = activeMethod === 'bit' ? validateBit() : validatePaypal();
    if (validationError) {
      setError(validationError);
      return;
    }
    await runGatewayPay();
  };

  const onMethodChange = (next: PaymentMethod) => {
    setMethod(next);
    setError(null);
    setCardFieldErrors({});
    // Israel: choosing Card skips our form and starts PayPlus checkout immediately.
    if (isIsraelProfile && next === 'card' && !processing) {
      void runGatewayPay();
    }
  };

  const clearCardFieldError = (key: keyof CardPaymentFieldErrors) => {
    setCardFieldErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setError(null);
  };

  const payLabel = amountLabel
    ? t('bookings.payment.pay', { amount: amountLabel })
    : t('bookings.payment.payNoAmount');

  const walletCreditsLabel = amountLabel
    ? t('bookings.walletCredits.pay', { amount: amountLabel })
    : t('bookings.walletCredits.payNoAmount');

  const fieldError = (key: keyof CardPaymentFieldErrors) => cardFieldErrors[key];

  const nationalIdField = isIsraelProfile ? (
    <label className={styles.field} htmlFor={`${fieldId}-national-id`}>
      <span className={styles.fieldLabel}>{t('bookings.payment.nationalId')}</span>
      <input
        id={`${fieldId}-national-id`}
        className={`${styles.input} ${styles.inputLtr}${fieldError('nationalId') ? ` ${styles.inputInvalid}` : ''}`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        dir="ltr"
        required
        placeholder={t('bookings.payment.nationalIdPlaceholder')}
        value={nationalId}
        onChange={(e) => {
          setNationalId(digitsOnly(e.target.value).slice(0, 9));
          clearCardFieldError('nationalId');
        }}
        disabled={processing}
        aria-invalid={fieldError('nationalId') ? true : undefined}
        aria-describedby={fieldError('nationalId') ? `${fieldId}-national-id-err` : undefined}
      />
      {fieldError('nationalId') ? (
        <span className={styles.fieldError} id={`${fieldId}-national-id-err`} role="alert">
          {fieldError('nationalId')}
        </span>
      ) : null}
    </label>
  ) : null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('bookings.payment.title')}
      dir={dir}
    >
      <div className={styles.sheet}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>{t('bookings.payment.title')}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            disabled={processing}
            aria-label={t('bookings.payment.cancel')}
          >
            ✕
          </button>
        </div>

        <div className={styles.amountBox}>
          <div className={styles.amountSummary}>
            <span className={styles.amountLabel}>{t('bookings.payment.amountDue')}</span>
            <span className={styles.amountVatNote}>{t('bookings.payment.vatIncluded')}</span>
          </div>
          <span className={styles.amountValue}>{amountLabel ?? '—'}</span>
        </div>

        <p className={styles.subtitle}>{t(subtitleKey)}</p>

        <fieldset className={styles.methodFieldset}>
          <legend className={styles.methodLegend}>{t('bookings.payment.methodLegend')}</legend>
          <div className={styles.methodRow} role="group" aria-label={t('bookings.payment.methodLegend')}>
            {methods.map((m) => {
              const labelKey =
                m === 'wallet'
                  ? 'bookings.walletCredits.method'
                  : m === 'card'
                    ? 'bookings.payment.methodCard'
                    : m === 'bit'
                      ? 'bookings.payment.methodBit'
                      : 'bookings.payment.methodPaypal';
              const active = activeMethod === m;
              return (
                <button
                  key={m}
                  type="button"
                  className={`${styles.methodButton}${active ? ` ${styles.methodButtonActive}` : ''}${
                    m === 'bit' ? ` ${styles.methodButtonBit}` : ''
                  }${m === 'paypal' ? ` ${styles.methodButtonPaypal}` : ''}`}
                  aria-pressed={active}
                  disabled={processing}
                  onClick={() => onMethodChange(m)}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </fieldset>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {activeMethod === 'wallet' ? (
            <>
              {walletCreditsBalanceLabel ? (
                <p className={styles.savedCardHint} role="status">
                  {t('bookings.walletCredits.balance', { amount: walletCreditsBalanceLabel })}
                </p>
              ) : null}
              <p className={styles.secureNote}>{t('bookings.walletCredits.hint')}</p>
            </>
          ) : null}
          {activeMethod === 'card' && !israelCardGoesToPayplus ? (
            <>
              {savedCards.length > 0 ? (
                <fieldset className={styles.savedCardsFieldset}>
                  <legend className={styles.savedCardsLegend}>
                    {t('bookings.payment.savedCardsLegend')}
                  </legend>
                  <div className={styles.savedCardsList}>
                    {savedCards.map((card) => {
                      const selected =
                        cardEntryMode === 'saved' && selectedSavedCardId === card.id;
                      return (
                        <button
                          key={card.id}
                          type="button"
                          className={`${styles.savedCardOption}${
                            selected ? ` ${styles.savedCardOptionActive}` : ''
                          }`}
                          aria-pressed={selected}
                          disabled={processing}
                          onClick={() => {
                            setCardEntryMode('saved');
                            setSelectedSavedCardId(card.id);
                            setCvc('');
                            setCardFieldErrors({});
                            setError(null);
                            if (card.nationalId) {
                              setNationalId(card.nationalId);
                            }
                          }}
                        >
                          <PaymentCardBrandIcon brand={card.brand} />
                          <span className={styles.savedCardText} dir="ltr">
                            <span className={styles.savedCardLast4}>
                              {formatPaymentCardLast4Display(card.last4)}
                            </span>
                            <span className={styles.savedCardMeta}>
                              {card.expiry}
                              {card.isPrimary
                                ? ` · ${t('profile.paymentMethods.primary')}`
                                : ''}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={`${styles.savedCardOption}${
                        cardEntryMode === 'new' ? ` ${styles.savedCardOptionActive}` : ''
                      }`}
                      aria-pressed={cardEntryMode === 'new'}
                      disabled={processing}
                      onClick={() => {
                        setCardEntryMode('new');
                        setSelectedSavedCardId(null);
                        setCvc('');
                        setCardFieldErrors({});
                        setError(null);
                      }}
                    >
                      <PaymentCardBrandIcon brand="unknown" />
                      <span className={styles.savedCardText}>
                        {t('bookings.payment.useNewCard')}
                      </span>
                    </button>
                  </div>
                </fieldset>
              ) : null}
              {nationalIdField}
              {usingSavedCard && selectedSavedCard ? (
                <>
                  <p className={styles.savedCardHint} role="status">
                    {t('bookings.payment.usingSavedCard', {
                      last4: selectedSavedCard.last4,
                      name: selectedSavedCard.cardholderName,
                    })}
                  </p>
                  <label className={styles.field} htmlFor={`${fieldId}-cvc`}>
                    <span className={styles.fieldLabel}>{t('bookings.payment.cvc')}</span>
                    <input
                      id={`${fieldId}-cvc`}
                      className={`${styles.input} ${styles.inputLtr}${fieldError('cvc') ? ` ${styles.inputInvalid}` : ''}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      dir="ltr"
                      required
                      placeholder="123"
                      value={cvc}
                      onChange={(e) => {
                        setCvc(digitsOnly(e.target.value).slice(0, 4));
                        clearCardFieldError('cvc');
                      }}
                      disabled={processing}
                      aria-invalid={fieldError('cvc') ? true : undefined}
                    />
                    {fieldError('cvc') ? (
                      <span className={styles.fieldError} role="alert">
                        {fieldError('cvc')}
                      </span>
                    ) : null}
                  </label>
                </>
              ) : (
                <>
              <label className={styles.field} htmlFor={`${fieldId}-name`}>
                <span className={styles.fieldLabel}>{t('bookings.payment.cardName')}</span>
                <input
                  id={`${fieldId}-name`}
                  className={`${styles.input}${fieldError('cardName') ? ` ${styles.inputInvalid}` : ''}`}
                  type="text"
                  autoComplete="cc-name"
                  required
                  placeholder={t('bookings.payment.cardNamePlaceholder')}
                  value={cardName}
                  onChange={(e) => {
                    setCardName(e.target.value);
                    clearCardFieldError('cardName');
                  }}
                  disabled={processing}
                  aria-invalid={fieldError('cardName') ? true : undefined}
                />
                {fieldError('cardName') ? (
                  <span className={styles.fieldError} role="alert">
                    {fieldError('cardName')}
                  </span>
                ) : null}
              </label>
              <label className={styles.field} htmlFor={`${fieldId}-number`}>
                <span className={styles.fieldLabel}>{t('bookings.payment.cardNumber')}</span>
                <div className={styles.cardNumberRow}>
                  <input
                    id={`${fieldId}-number`}
                    className={`${styles.input} ${styles.inputLtr}${fieldError('cardNumber') ? ` ${styles.inputInvalid}` : ''}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    dir="ltr"
                    required
                    placeholder="0000 0000 0000 0000"
                    value={cardNumber}
                    onChange={(e) => {
                      setCardNumber(formatCardNumber(e.target.value));
                      clearCardFieldError('cardNumber');
                    }}
                    disabled={processing}
                    aria-invalid={fieldError('cardNumber') ? true : undefined}
                  />
                  <PaymentCardBrandIcon cardNumber={cardNumber} />
                </div>
                {fieldError('cardNumber') ? (
                  <span className={styles.fieldError} role="alert">
                    {fieldError('cardNumber')}
                  </span>
                ) : null}
              </label>
              <div className={styles.fieldRow}>
                <label className={styles.field} htmlFor={`${fieldId}-expiry`}>
                  <span className={styles.fieldLabel}>{t('bookings.payment.expiry')}</span>
                  <input
                    id={`${fieldId}-expiry`}
                    className={`${styles.input} ${styles.inputLtr}${fieldError('expiry') ? ` ${styles.inputInvalid}` : ''}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    dir="ltr"
                    required
                    placeholder="MM/YY"
                    value={expiry}
                    onChange={(e) => {
                      setExpiry(formatExpiry(e.target.value));
                      clearCardFieldError('expiry');
                    }}
                    disabled={processing}
                    aria-invalid={fieldError('expiry') ? true : undefined}
                  />
                  {fieldError('expiry') ? (
                    <span className={styles.fieldError} role="alert">
                      {fieldError('expiry')}
                    </span>
                  ) : null}
                </label>
                <label className={styles.field} htmlFor={`${fieldId}-cvc`}>
                  <span className={styles.fieldLabel}>{t('bookings.payment.cvc')}</span>
                  <input
                    id={`${fieldId}-cvc`}
                    className={`${styles.input} ${styles.inputLtr}${fieldError('cvc') ? ` ${styles.inputInvalid}` : ''}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    dir="ltr"
                    required
                    placeholder="123"
                    value={cvc}
                    onChange={(e) => {
                      setCvc(digitsOnly(e.target.value).slice(0, 4));
                      clearCardFieldError('cvc');
                    }}
                    disabled={processing}
                    aria-invalid={fieldError('cvc') ? true : undefined}
                  />
                  {fieldError('cvc') ? (
                    <span className={styles.fieldError} role="alert">
                      {fieldError('cvc')}
                    </span>
                  ) : null}
                </label>
              </div>
                </>
              )}
            </>
          ) : null}

          {activeMethod === 'bit' ? (
            <>
              {nationalIdField}
              <label className={styles.field} htmlFor={`${fieldId}-bit-phone`}>
                <span className={styles.fieldLabel}>{t('bookings.payment.bitPhone')}</span>
                <input
                  id={`${fieldId}-bit-phone`}
                  className={`${styles.input} ${styles.inputLtr}`}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  dir="ltr"
                  required
                  placeholder={t('bookings.payment.bitPhonePlaceholder')}
                  value={bitPhone}
                  onChange={(e) => setBitPhone(formatIsraeliMobile(e.target.value))}
                  disabled={processing}
                />
              </label>
            </>
          ) : null}

          {activeMethod === 'paypal' ? (
            <label className={styles.field} htmlFor={`${fieldId}-paypal-email`}>
              <span className={styles.fieldLabel}>{t('bookings.payment.paypalEmail')}</span>
              <input
                id={`${fieldId}-paypal-email`}
                className={styles.input}
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder={t('bookings.payment.paypalEmailPlaceholder')}
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                disabled={processing}
              />
            </label>
          ) : null}

          {error && (activeMethod !== 'card' || israelCardGoesToPayplus) ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
          {error &&
          activeMethod === 'card' &&
          !israelCardGoesToPayplus &&
          cardPaymentIsValid(cardFieldErrors) ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className={styles.payButton} disabled={processing}>
            {processing
              ? t('bookings.payment.processing')
              : activeMethod === 'wallet'
                ? walletCreditsLabel
                : israelCardGoesToPayplus
                  ? t('bookings.payplus.payNow')
                  : payLabel}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={processing}
          >
            {t('bookings.payment.cancel')}
          </button>

          <p className={styles.secureNote}>
            {activeMethod === 'wallet'
              ? t('bookings.walletCredits.secureNote')
              : israelCardGoesToPayplus
                ? t('bookings.payment.secureNote')
                : activeMethod === 'card' && !usingSavedCard && isPayplusTestCardNumber(cardNumber)
                  ? t('bookings.payplus.testChargeNotice')
                  : t('bookings.payment.secureNote')}
          </p>
        </form>
      </div>
    </div>
  );
}
